/**
 * 커서가 있는 문장 — 「현재 문장 실행」 · 실행계획 · 바인드 편집이 함께 쓴다 (2026-10-02).
 *
 * 예전에는 SQLEditor 에 두 벌, App 에 한 벌 사본이 있었고 셋 다 **따옴표만 보고 주석은 몰랐다.**
 * 그래서 `-- it's` 같은 주석 하나가 따옴표를 열어 그 뒤 문장 경계를 통째로 놓쳤다. 책갈피
 * (`--@ 이름`)가 생기면서 주석 줄이 흔해져 한곳으로 모았다 — 문장 나누기는 주석·따옴표를 아는
 * `parseStatements`(sqlBinds) 한 곳이다.
 *
 * **주석만 있는 조각은 실행 대상이 아니다.** 마지막 `;` 뒤의 `--@ 다음 작업` 같은 줄에 커서가
 * 있으면 그 주석을 서버로 보냈다(빈 문장). 이제 가장 가까운 진짜 문장을 고른다.
 * 번호(index)는 parseStatements 의 순서 그대로다 — 문장별 바인드 값이 이 번호로 저장된다.
 */
import { parseStatements } from './sqlBinds';

/**
 * 주석을 지운 SQL — 문자열 안의 `--`·`/*` 는 건드리지 않는다.
 * **옵티마이저 힌트(`/*+ … *\/`, `--+ …`)는 주석이 아니라 지시다 — 남긴다.** 지우면 에러 없이 실행계획만 바뀐다.
 */
export function stripSqlComments(text) {
    if (!text) return '';
    let out = '';
    const n = text.length;
    let i = 0;
    while (i < n) {
        const ch = text[i];
        if (ch === "'") {
            let j = i + 1;
            while (j < n) {
                if (text[j] === "'") {
                    if (text[j + 1] === "'") { j += 2; continue; }
                    j++; break;
                }
                j++;
            }
            out += text.slice(i, j);
            i = j;
            continue;
        }
        if (ch === '"') {
            let j = i + 1;
            while (j < n && text[j] !== '"') j++;
            out += text.slice(i, j + 1);
            i = j + 1;
            continue;
        }
        if (ch === '-' && text[i + 1] === '-') {
            let j = i;
            while (j < n && text[j] !== '\n') j++;
            if (text[i + 2] === '+') out += text.slice(i, j);   // --+ 힌트
            i = j;
            continue;
        }
        if (ch === '/' && text[i + 1] === '*') {
            let j = i + 2;
            while (j < n && !(text[j] === '*' && text[j + 1] === '/')) j++;
            j += 2;
            out += text[i + 2] === '+' ? text.slice(i, j) : ' ';   // /*+ 힌트 */ 는 그대로
            i = j;
            continue;
        }
        out += ch;
        i++;
    }
    return out;
}

/**
 * 결과 메시지에 붙일 문장 앞머리 — 주석을 빼고 공백을 한 칸으로 줄여 max 자까지.
 * 「실행되었습니다」 만으로는 **무엇이** 실행됐는지 몰라, 다른 문장이 돌아도 알아챌 수 없었다(2026-10-05).
 */
export function statementLabel(sql, max = 60) {
    const flat = stripSqlComments(sql || '').replace(/\s+/g, ' ').trim().replace(/;$/, '');
    return flat.length > max ? `${flat.slice(0, max).trimEnd()}…` : flat;
}

/**
 * 처리 건수를 말할 DML 종류 — INSERT · UPDATE · DELETE · MERGE, 아니면 null.
 * WITH … INSERT 처럼 CTE 를 앞에 둔 꼴은 드물어 보지 않는다(그때는 예전처럼 「실행되었습니다」).
 */
export function dmlVerb(sql) {
    const m = /^\s*(INSERT|UPDATE|DELETE|MERGE)\b/i.exec(stripSqlComments(sql || ''));
    return m ? m[1].toUpperCase() : null;
}

/** 주석만 있는 조각인가 */
export const isCommentOnly = (text) => !stripSqlComments(text).trim();

/**
 * @returns {{ text, index, start, end } | null}  커서가 속한 문장, 없으면 가장 가까운 문장
 */
export function statementAtCursor(docText, cursor) {
    if (!docText || !docText.trim()) return null;
    const all = parseStatements(docText).map((s, index) => ({ ...s, index }));
    const real = all.filter(s => !isCommentOnly(s.text));
    if (real.length === 0) return null;
    const pick = (s) => ({ text: s.text, index: s.index, start: s.start, end: s.end });
    // **실제로 보이는 범위**로 판정한다(2026-10-05). parseStatements 의 start 는 「앞 문장의 ; 바로 다음」 이라
    // 줄바꿈부터 시작하고 end 는 자기 ; 의 위치다. 그래서 `…FROM ot.orders;|` 처럼 **줄 끝 ; 바로 뒤**에 커서가
    // 있으면 그 자리가 다음 문장의 start 와 같아 **다음 문장이 실행됐다** — 사용자는 「실행됐다」 를 보고 1번 표가
    // 만들어진 줄 알았는데 실제로는 2번 문장이 돌았다(실측: CREATE TABLE pr_orders 대신 pr_order_items).
    // 보이는 범위 = 첫 글자(앞 공백·줄바꿈 제외) ~ 자기 ; 바로 뒤. 경계(`a;|b`)에서는 앞 문장이 이긴다.
    const ranges = real.map((s) => {
        const raw = docText.substring(s.start, s.end);
        const from = s.start + (raw.length - raw.trimStart().length);
        const to = docText[s.end] === ';' ? s.end + 1 : s.start + raw.trimEnd().length;
        return { s, from, to };
    });
    const inside = ranges.find(r => cursor >= r.from && cursor <= r.to);
    if (inside) return pick(inside.s);
    // 문장 사이 빈 줄·뒤쪽 주석 줄 — 가장 가까운 문장(같으면 앞 문장)
    let best = ranges[0];
    let bestDist = Infinity;
    for (const r of ranges) {
        const d = cursor < r.from ? r.from - cursor : cursor - r.to;
        if (d < bestDist) { bestDist = d; best = r; }
    }
    return pick(best.s);
}
